import { notification } from "antd";
import cuid from "cuid";
import { defaults, get, map } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation } from "shared/hooks/useApi";
import { useSignUpAuth } from "shared/hooks/useSignUpAuth";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  INITIAL_VALUES,
  ORGANIZATION_TYPES,
  SCREENS,
  USER_TYPES,
} from "utils/constants";
import View from "./View";

const Create = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const FORM = t("FORM", { returnObjects: true });
  const { t: errors } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = errors("API", { returnObjects: true });
  const navigate = useNavigate();
  const [userId, orgId] = useMemo(() => [cuid(), cuid()], []);
  const cancel = () => navigate(SCREENS.PRIVATE.ADMIN.ORGANIZATIONS.LIST.path);
  const [signUpAuth] = useSignUpAuth({
    onError: () => notification.error({ message: FORM.IN_USE }),
  });
  const [updateUser] = useMutation(graphql.mutations.UPDATE_USER_AUTH);

  const [signUp] = useMutation(graphql.mutations.SIGN_UP);

  const [addOrganization] = useMutation(graphql.mutations.CREATE_ORGANIZATION, {
    refetchQueries: [
      {
        query: graphql.queries.ORGANIZATIONS,
        awaitRefetchQueries: true,
        variables: {
          where: { type: ORGANIZATION_TYPES.INS },
          ...INITIAL_VALUES,
        },
      },
    ],
    onCompleted: () => {
      navigate(SCREENS.PRIVATE.ADMIN.ORGANIZATIONS.LIST.path);
    },
  });

  const onError = (id) => {
    updateUser({
      variables: {
        where: { id },
        data: {
          phone: null,
          username: null,
          email: null,
          password: null,
        },
      },
    });
    notification.error({
      message: API_ERRORS.GENERIC,
    });
  };

  const onSubmit = ({ telecoms, organization, iconAttachment, user }) => {
    const organizationTelecoms = map(telecoms, (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );
    signUpAuth({
      variables: {
        data: {
          id: userId,
          username: user.email,
          email: user.email,
          phone: user.phone,
          password: cuid(),
          meta: { type: USER_TYPES.INSURANCE_ADMIN.type },
          organization: {
            id: orgId,
            name: organization.name,
          },
        },
      },
      onCompleted: ({ signUp: authResult }) =>
        signUp({
          variables: {
            data: {
              user: {
                id: userId,
                ...user,
                username: user.email,
                meta: {
                  type: USER_TYPES.INSURANCE_ADMIN.type,
                  ...get(authResult, "meta", {}),
                },
              },
              baseUrl: process.env.REACT_APP_BASE_URL,
            },
          },
          onError: () => onError(userId),
          onCompleted: () => {
            addOrganization({
              variables: {
                data: {
                  id: orgId,
                  ...organization,
                  telecoms: organizationTelecoms,
                  users: [{ id: userId }],
                  icon: iconAttachment,
                },
              },
              onError: () => onError(userId),
            });
          },
        }),
    });
  };

  return <View onSubmit={onSubmit} cancel={cancel} />;
};

export default Create;
